import type { Locale } from "../i18n/config";
import { dictionaries } from "../i18n/dictionaries";
import type { PostBase, ResolvedPost } from "./types";

// ── Shared post registry ─────────────────────────────────────────────────────
// Non-translatable facts only (slug, date, hero image, tags, author). The
// translatable parts — title, excerpt, heroAlt, reading time, and the body —
// live per language under `i18n/locales/<lang>/posts/<slug>.tsx` and are keyed
// by slug in each locale's `posts` map.
//
// To add a post:
//   1. Add a `PostBase` entry below (import its hero image here).
//   2. Create `i18n/locales/<lang>/posts/<slug>.tsx` for every language and
//      register it in that locale's `posts/index.ts`.
import heroUiProblem from "../assets/blog/moebius-09.jpg";
import heroFinishedWork from "../assets/blog/valley-finished-work.jpg";
import heroVibecoding from "../assets/blog/moebius-vibecoding.jpg";
import heroFakeBenchmarks from "../assets/blog/moebius-14.jpg";
import heroAgentLoops from "../assets/blog/moebius-15.jpg";
import heroAgentObservability from "../assets/blog/moebius-13.jpg";
import heroDashboards from "../assets/blog/moebius-16.jpg";

const bases: PostBase[] = [
  { slug: "why-llms-cannot-design-dashboards", date: "2026-07-22", hero: heroDashboards, tags: ["design", "agents", "dashboards", "essay"], author: "Paul Horn" },
  { slug: "observing-six-coding-agent-clis", date: "2026-07-22", hero: heroAgentObservability, tags: ["agents", "terminals", "hooks", "engineering"] },
  { slug: "designing-an-agent-loop-for-coding", date: "2026-07-12", hero: heroAgentLoops, tags: ["agents", "workflows", "verification", "engineering"], author: "Paul Horn" },
  { slug: "the-benchmarks-are-fake", date: "2026-06-08", hero: heroFakeBenchmarks, tags: ["agents", "benchmarks", "evaluation", "essay"], author: "Paul Horn", draft: true },
  { slug: "vibecoding-addiction", date: "2026-06-12", hero: heroVibecoding, tags: ["agents", "psychology", "essay"], author: "Paul Horn" },
  { slug: "illusion-of-finished-work", date: "2026-06-06", hero: heroFinishedWork, tags: ["agents", "review", "essay"], author: "Paul Horn" },
  { slug: "ui-problem-ai-coding-agents", date: "2026-05-31", hero: heroUiProblem, tags: ["agents", "design", "essay"], author: "Paul Horn" },
];

// ─────────────────────────────────────────────────────────────────────────────

/** Merge a base with its translation for `locale`, or `undefined` if untranslated. */
function resolve(base: PostBase, locale: Locale): ResolvedPost | undefined {
  const loc = dictionaries[locale].posts[base.slug];
  if (!loc) return undefined;
  return { ...base, ...loc };
}

/** All published posts (drafts excluded) for `locale`, newest first. */
export function getPosts(locale: Locale): ResolvedPost[] {
  return bases
    .filter((b) => !b.draft)
    .map((b) => resolve(b, locale))
    .filter((p): p is ResolvedPost => Boolean(p))
    .sort((a, b) => b.date.localeCompare(a.date));
}

/** Look up a published post by its slug in `locale`. */
export function getPost(locale: Locale, slug: string): ResolvedPost | undefined {
  return getPosts(locale).find((p) => p.slug === slug);
}

/**
 * Posts related to `slug`, ranked by number of shared tags (newest breaks ties).
 * Falls back to the most recent other posts so the section is never empty.
 */
export function relatedPosts(locale: Locale, slug: string, limit = 3): ResolvedPost[] {
  const posts = getPosts(locale);
  const current = posts.find((p) => p.slug === slug);
  if (!current) return [];
  const tags = new Set(current.tags ?? []);
  const others = posts.filter((p) => p.slug !== slug);

  const scored = others
    .map((p) => ({ post: p, shared: (p.tags ?? []).filter((t) => tags.has(t)).length }))
    .sort((a, b) => b.shared - a.shared || b.post.date.localeCompare(a.post.date));

  return scored.slice(0, limit).map((s) => s.post);
}
